import { createIdempotencyKey } from "./idempotency";

const DB_NAME = "menu-zyka-offline";
const DB_VERSION = 1;
const STORE_NAME = "operations";
export const OFFLINE_QUEUE_EVENT = "menuzyka:offline-queue-change";
const MAX_ATTEMPTS = 5;

let processor = null;
let ownerProvider = null;
let syncPromise = null;

const emitChange = () => window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_EVENT));

const openDatabase = () => new Promise((resolve, reject) => {
  if (!window.indexedDB) {
    reject(new Error("Offline storage is unavailable in this browser."));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onerror = () => reject(request.error);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      const store = database.createObjectStore(STORE_NAME, { keyPath: "id" });
      store.createIndex("status", "status", { unique: false });
      store.createIndex("owner", "owner", { unique: false });
      store.createIndex("createdAt", "createdAt", { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
});

const transact = async (mode, action) => {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      let result;
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error("Offline storage transaction was cancelled."));
      result = action(store);
    });
  } finally {
    database.close();
  }
};

export const listOfflineOperations = async () => {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result.sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
    });
  } finally {
    database.close();
  }
};

export const listVisibleOfflineOperations = async () => {
  const operations = await listOfflineOperations();
  const owner = ownerProvider?.();
  return owner ? operations.filter(operation => operation.owner === owner) : operations;
};

export const queueOfflineRequest = async ({ urlExtension, params, method, owner, label, idempotencyKey }) => {
  const now = new Date().toISOString();
  const operation = {
    id: idempotencyKey || createIdempotencyKey("offline"),
    idempotencyKey: idempotencyKey || createIdempotencyKey("offline"),
    urlExtension,
    params,
    method,
    owner,
    label: label || "Order submission",
    status: "pending",
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    nextAttemptAt: now,
    lastError: null,
  };
  operation.idempotencyKey = operation.id;
  await transact("readwrite", store => store.put(operation));
  emitChange();
  return operation;
};

const saveOperation = async operation => {
  operation.updatedAt = new Date().toISOString();
  await transact("readwrite", store => store.put(operation));
};

export const removeOfflineOperation = async id => {
  await transact("readwrite", store => store.delete(id));
  emitChange();
};

export const retryOfflineOperation = async id => {
  const operations = await listOfflineOperations();
  const operation = operations.find(item => item.id === id);
  if (!operation) return;
  operation.status = "pending";
  operation.attempts = 0;
  operation.lastError = null;
  operation.nextAttemptAt = new Date().toISOString();
  await saveOperation(operation);
  emitChange();
  await syncOfflineOperations();
};

export const setOfflineRequestProcessor = (value, currentOwnerProvider = null) => {
  processor = value;
  ownerProvider = currentOwnerProvider;
};

export const syncOfflineOperations = async () => {
  if (syncPromise) return syncPromise;
  if (!processor || !navigator.onLine) return [];
  syncPromise = (async () => {
    const owner = ownerProvider?.();
    const operations = (await listOfflineOperations()).filter(operation => !owner || operation.owner === owner);
    const now = Date.now();
    for (const operation of operations) {
      if (operation.status === "failed" || Date.parse(operation.nextAttemptAt) > now) continue;
      try {
        operation.status = "syncing";
        await saveOperation(operation);
        emitChange();
        const response = await processor(operation);
        if (response.ok) {
          await removeOfflineOperation(operation.id);
          continue;
        }
        const details = await response.text();
        operation.attempts += 1;
        operation.lastError = details || `Server returned ${response.status}.`;
        const retryable = response.status >= 500 || response.status === 408 || response.status === 409 || response.status === 429;
        operation.status = retryable && operation.attempts < MAX_ATTEMPTS ? "pending" : "failed";
      } catch (error) {
        operation.attempts += 1;
        operation.lastError = error?.message || "Network request failed.";
        operation.status = operation.attempts < MAX_ATTEMPTS ? "pending" : "failed";
      }
      const delaySeconds = Math.min(60, 2 ** operation.attempts);
      operation.nextAttemptAt = new Date(Date.now() + delaySeconds * 1000).toISOString();
      await saveOperation(operation);
      emitChange();
      if (!navigator.onLine) break;
    }
    return listOfflineOperations();
  })().finally(() => { syncPromise = null; });
  return syncPromise;
};

export const queuedResponse = operation => new Response(JSON.stringify({
  queued: true,
  operationId: operation.id,
  message: "Order saved on this device and will sync when the connection returns.",
}), { status: 202, headers: { "Content-Type": "application/json", "X-Offline-Queued": "true" } });
